import { useEffect, useState } from 'react';

/**
 * Returns true only on devices that support a true hover pointer (i.e.
 * desktop with a mouse). On touch-only devices iOS Safari emulates
 * `mouseenter` on first tap and *suppresses* the click that follows —
 * users see a hover effect and have to tap again to actually click.
 *
 * Gate `onMouseEnter`/`onMouseLeave` handlers behind this so touch users
 * get the click through on the first tap.
 */
export function useSupportsHover(): boolean {
  const [supports, setSupports] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(hover: hover)')?.matches ?? false;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(hover: hover)');
    const onChange = () => setSupports(mql.matches);
    // Safari ≥14 supports addEventListener; older falls back to deprecated API.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return supports;
}
